import { Component, forwardRef, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Icons } from '../../../constants/icons.constants';
import { Masks } from '../../../constants/mask.constants';
import { BaseFormControlComponent } from '../base-form-control.component';

@Component({
    selector: 'single-date-input',
    template: `
        <div class="single-date-input">
            <lsq-label
                *ngIf="label"
                [label]="label"
                [subLabel]="subLabel"
                [style.marginBottom]="'1rem'"
            ></lsq-label>
            <div
                class="lsq-form-control"
                [class.focused]="isFocused"
                [class.disabled]="disabled"
                [class.has-error]="showErrorMessage(shouldValidate())"
                (click)="handleFocus()"
            >
                <div class="form-control-prefix">
                    <mat-datepicker-toggle [for]="picker" [disableRipple]="true">
                        <mat-icon matDatepickerToggleIcon>
                            <lsq-icon [type]="calendarIcon" [height]="16"></lsq-icon>
                        </mat-icon>
                    </mat-datepicker-toggle>
                    <input
                        id="hiddenDateInput"
                        [matDatepicker]="picker"
                        [matDatepickerFilter]="dateFilter"
                        (dateChange)="handleDatePicked($event)"
                        [(ngModel)]="model"
                    />
                </div>
                <lsq-base-input
                    [name]="name"
                    [value]="model | date: 'MM/dd/yyyy':'UTC'"
                    [placeholder]="placeholder"
                    [disabled]="disabled"
                    [mask]="dateMask"
                    [hasError]="showErrorMessage(shouldValidate())"
                    (blurred)="handleInputBlur()"
                    (valueChanged)="handleInputChange($event)"
                ></lsq-base-input>
            </div>
            <mat-datepicker #picker></mat-datepicker>
            <div class="lsq-form-control-error-message" *ngIf="showErrorMessage(shouldValidate())">
                {{ getErrorMessage() }}
            </div>
        </div>
    `,
    encapsulation: ViewEncapsulation.None,
    styleUrls: [
        '../common-styles/lsq-common-form-control.scss',
        './single-date-input.component.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SingleDateInputComponent),
            multi: true
        }
    ]
})
export class SingleDateInputComponent extends BaseFormControlComponent<any>
    implements OnInit, ControlValueAccessor {
    @Input() public name!: string;
    @Input() public label?: string;
    @Input() public subLabel?: string;
    @Input() public placeholder?: string;
    @Input() public allowFuture = true;
    @Input() public allowPast = true;
    @Input() public customDateFilter?: (d: Date | null) => boolean;

    public currentValue: Date | null = new Date();

    // Date filter helpers.
    public readonly today = new Date();
    private readonly _noFutureDatesFilter = (date: Date): boolean => date <= this.today;
    private readonly _noPastDatesFilter = (date: Date): boolean => {
        const yesterday = new Date();
        yesterday.setDate(this.today.getDate() - 1);
        return date >= yesterday;
    };

    // This gets passed to matDatePickerFilter, which runs it on every date in the
    // current view of the date picker to determine if it is a valid selectable date.
    // https://material.angular.io/components/datepicker/overview#datepicker-filter
    private _dateFilter = (date: Date): boolean => {
        const filterResults: boolean[] = [];

        if (!!this.customDateFilter) {
            filterResults.push(this.customDateFilter(date));
        }
        if (!this.allowFuture) {
            filterResults.push(this._noFutureDatesFilter(date));
        }
        if (!this.allowPast) {
            filterResults.push(this._noPastDatesFilter(date));
        }

        // Only return true if every applied filter returned true (or there were no filters).
        return filterResults.length === 0 || filterResults.every((result) => result);
    };

    public readonly dateFilter = this._dateFilter.bind(this);

    public isFocused = false;
    public hasInitialBlurHappened = false;

    public readonly calendarIcon = Icons.FaCalendarAlt;
    public readonly dateMask = Masks.DateMask;

    public ngOnInit(): void {
        if (this.group?.get(this.formControlName)?.value) {
            this.handleChange(this.group?.get(this.formControlName)?.value);
        }
    }

    public handleFocus(): void {
        this.isFocused = true;
    }

    public handleDatePicked(pickedDate: MatDatepickerInputEvent<Date>) {
        this.currentValue = pickedDate.value;
        this.handleInputBlur();
    }

    public handleInputChange(valueChangedEvent: { value: string; isAutofilled: boolean }): void {
        if (valueChangedEvent.value !== '' || valueChangedEvent.value === null) {
            this.currentValue = new Date(valueChangedEvent.value);
        } else {
            this.currentValue = null;
        }
    }

    public handleInputBlur(): void {
        this.handleChange(this.currentValue);
        this.hasInitialBlurHappened = true;
        this.isFocused = false;
    }

    public shouldValidate(): boolean {
        return !this.isFocused || this.hasInitialBlurHappened;
    }
}
